/**
 * Workaround for a Swym bug: after the FIRST variant/size change on a PDP,
 * Swym's own re-render fails to reinsert its wishlist button and never
 * recovers for the rest of that page view (console shows "[SWYM] No
 * template-based button content found. Please configure custom HTML in
 * block settings.").
 *
 * Verified live DOM structure: the real button lives at
 *   <buy-buttons> > #swym-custom-pdp-button > .swym-button-content > button
 * A separate ".swym-wishlist-button-bar" div elsewhere in the form is NOT
 * where the working button renders — it's an unused placeholder from a
 * different (broken) Swym code path. Do not anchor to it.
 *
 * IMPORTANT: a variant change on this theme replaces the entire
 * `.shopify-product-form` element (Shopify's section-rendering fetch does
 * an innerHTML swap one level up), not just its contents. So this cannot
 * cache state per form-element or observe a specific form node directly —
 * both get thrown away with it. Instead this caches ONE global button
 * wrapper (there is only ever one product per PDP) and observes
 * document.body, re-querying the live form fresh on every mutation batch.
 *
 * Root cause (not fixed by this file): lives inside Swym's own swym-relay
 * extension bundle, not in this theme. See Swym Engineering escalation.
 */
(function () {
  'use strict';

  var WRAPPER_SELECTOR = '#swym-custom-pdp-button, .swym-button-content';
  var BUTTON_SELECTOR = '#swym-atw-pdp-button, .swym-button-content button, button.swym-icon-text';
  var ANCHOR_SELECTOR = 'buy-buttons, .buy-buttons';
  var FORM_SELECTOR = '.shopify-product-form';

  var cachedWrapper = null;
  var pending = false;

  function getVariantIdFromForm(form) {
    var input = form.querySelector('input[name="id"]');
    return input ? input.value : null;
  }

  function buildProduct(variantId) {
    var variants = window.SwymProductVariants || {};
    var v = variants[variantId];
    if (!v) return null;
    return {
      epi: v.epi,
      empi: v.empi,
      du: v.du,
      dt: v.dt,
      iu: v.iu,
      pr: v.pr
    };
  }

  function setButtonVisualState(btn, added) {
    btn.setAttribute('aria-pressed', added ? 'true' : 'false');
    btn.classList.toggle('swym-added', added);
    var span = btn.querySelector('span');
    if (span) {
      span.textContent = added ? 'Added to Wishlist' : 'Add to Wishlist';
    }
    btn.setAttribute('aria-label', added ? 'Remove from wishlist' : 'Add to wishlist');
  }

  function wireButton(btn, form) {
    btn.addEventListener('click', function (evt) {
      evt.preventDefault();
      var swat = window._swat;
      if (!swat) return;

      var variantId = getVariantIdFromForm(form);
      var product = buildProduct(variantId);
      if (!product) return;

      var isAdded = btn.getAttribute('aria-pressed') === 'true';

      if (isAdded) {
        swat.removeFromWishList(product, function () {
          setButtonVisualState(btn, false);
        });
      } else {
        swat.addToWishList(product, function () {
          setButtonVisualState(btn, true);
        });
      }
    });

    var swat = window._swat;
    var variantId = getVariantIdFromForm(form);
    var product = buildProduct(variantId);
    if (swat && product && typeof swat.fetch === 'function') {
      swat.fetch(function (items) {
        var added = (items || []).some(function (i) {
          return i.empi === product.empi && i.epi === product.epi;
        });
        setButtonVisualState(btn, added);
      });
    }
  }

  function checkForms() {
    var forms = document.querySelectorAll(FORM_SELECTOR);
    forms.forEach(function (form) {
      var wrapper = form.querySelector(WRAPPER_SELECTOR);

      if (wrapper) {
        // Swym's own button is present and healthy right now — cache it fresh.
        cachedWrapper = wrapper.cloneNode(true);
        return;
      }

      // Wrapper missing entirely — this is the bug. Refill if possible.
      if (!cachedWrapper) return;
      var anchor = form.querySelector(ANCHOR_SELECTOR);
      if (!anchor) return;

      var clone = cachedWrapper.cloneNode(true);
      anchor.appendChild(clone);

      var btn = clone.matches(BUTTON_SELECTOR) ? clone : clone.querySelector(BUTTON_SELECTOR);
      if (btn) wireButton(btn, form);
    });
  }

  function scheduleCheck() {
    if (pending) return;
    pending = true;
    requestAnimationFrame(function () {
      pending = false;
      checkForms();
    });
  }

  function init() {
    checkForms();
    var observer = new MutationObserver(scheduleCheck);
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
